import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { User, X, Settings, MessageCircle, Camera, Check, Pencil, MapPin, Trash2, ChevronRight, ArrowLeft, Wallet, Bell, BellOff, LogOut, KeyRound, Send, FolderTree, Package, ShoppingBag, Newspaper, ImageIcon, Sun, Moon, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { BottomNavigation } from "@/components/BottomNavigation";
import { PageTransition } from "@/components/PageTransition";
import { MenuDrawer } from "@/components/MenuDrawer";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

import SavedPaymentMethods from "@/components/SavedPaymentMethods";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useAdminRole } from "@/hooks/useAdminRole";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import CategoryManager from "@/components/CategoryManager";
import ProductCategoryManager from "@/components/ProductCategoryManager";
import WallpaperCategoryManager from "@/components/WallpaperCategoryManager";
import UserOrders from "@/components/UserOrders";
import AdminOrders from "@/components/AdminOrders";
import NewsManager from "@/components/NewsManager";
import WallpaperDeleteManager from "@/components/WallpaperDeleteManager";
import AdminUsersTable from "@/components/AdminUsersTable";
import IPhoneImageManager from "@/components/IPhoneImageManager";
import { useTheme } from "@/hooks/useTheme";
import { Users as UsersIcon, Smartphone as SmartphoneIcon } from "lucide-react";

interface Profile {
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  username: string | null;
  messaging_enabled: boolean;
}

const Profile = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedFirstName, setEditedFirstName] = useState("");
  const [editedLastName, setEditedLastName] = useState("");
  const [editedUsername, setEditedUsername] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [locationPermission, setLocationPermission] = useState<string | null>(null);
  
  const [isPaymentMethodsOpen, setIsPaymentMethodsOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [isProductCategoryManagerOpen, setIsProductCategoryManagerOpen] = useState(false);
  const [isUserOrdersOpen, setIsUserOrdersOpen] = useState(false);
  const [isAdminOrdersOpen, setIsAdminOrdersOpen] = useState(false);
  const [isNewsManagerOpen, setIsNewsManagerOpen] = useState(false);
  const [isWallpaperCategoryManagerOpen, setIsWallpaperCategoryManagerOpen] = useState(false);
  const [isWallpaperDeleteManagerOpen, setIsWallpaperDeleteManagerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAdminUsersOpen, setIsAdminUsersOpen] = useState(false);
  const [isIPhoneImageManagerOpen, setIsIPhoneImageManagerOpen] = useState(false);
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, isLoading: pushLoading, toggleSubscription } = usePushNotifications();
  const { isAdmin } = useAdminRole(userId);
  const { preference: themePreference, setTheme } = useTheme();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      setEmail(session.user.email || null);
      setUserId(session.user.id);

      // Fetch profile data
      const { data: profileData } = await supabase
        .from("profiles")
        .select("first_name, last_name, avatar_url, username, messaging_enabled")
        .eq("user_id", session.user.id)
        .maybeSingle();

      setProfile(profileData);
      if (profileData) {
        setEditedFirstName(profileData.first_name || "");
        setEditedLastName(profileData.last_name || "");
        setEditedUsername(profileData.username || "");
      }

      setLoading(false);

      // Check location permission status
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          setLocationPermission(result.state);
          result.onchange = () => setLocationPermission(result.state);
        });
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLocationPermission = () => {
    navigator.geolocation.getCurrentPosition(
      () => {
        setLocationPermission('granted');
        toast.success("Posizione attivata");
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermission('denied');
          toast.error("Permesso posizione negato. Modifica dalle impostazioni del browser.");
        }
      }
    );
  };

  const handleClearCache = async () => {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear caches if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      toast.success("Cache svuotata con successo");
    } catch (error) {
      console.error("Error clearing cache:", error);
      toast.error("Errore durante la pulizia della cache");
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine valida");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'immagine deve essere inferiore a 5MB");
      return;
    }

    setIsSaving(true);

    try {
      const fileExt = file.name.split(".").pop();
      const filePath = `${userId}/${Date.now()}.${fileExt}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", userId);

      if (updateError) throw updateError;

      setProfile(prev => prev ? { ...prev, avatar_url: publicUrl } : null);
      toast.success("Foto profilo aggiornata");
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error("Errore durante il caricamento dell'immagine");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditName = () => {
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!userId) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          first_name: editedFirstName.trim() || null, 
          last_name: editedLastName.trim() || null,
          username: editedUsername.trim() || null,
        })
        .eq("user_id", userId);

      if (error) {
        if (error.code === '23505') {
          toast.error("Username già in uso, scegline un altro");
          return;
        }
        throw error;
      }

      setProfile(prev => prev ? { 
        ...prev, 
        first_name: editedFirstName.trim() || null, 
        last_name: editedLastName.trim() || null,
        username: editedUsername.trim() || null,
      } : null);
      setIsEditingName(false);
      toast.success("Profilo aggiornato");
    } catch (error) {
      console.error("Error updating name:", error);
      toast.error("Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Compila tutti i campi");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }

    setIsChangingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success("Password modificata con successo");
      setIsChangePasswordOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast.error(error.message || "Errore durante il cambio password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const sendAdminPushNotification = async () => {
    if (!notificationTitle.trim() || !notificationBody.trim()) {
      toast.error("Inserisci titolo e messaggio della notifica");
      return;
    }

    setSendingNotification(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          type: 'all',
          payload: {
            title: notificationTitle,
            body: notificationBody,
            icon: '/favicon.ico',
            url: '/'
          }
        }
      });

      if (error) throw error;

      toast.success(`Notifica inviata a ${data?.sent || 0} utenti`);
      setNotificationTitle("");
      setNotificationBody("");
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Errore durante l'invio della notifica");
    } finally {
      setSendingNotification(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || "Utente";

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-32">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-lg border-b border-border/50">
          <div className="container mx-auto px-4 py-4 flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="p-2 rounded-full hover:bg-muted transition-all duration-300 hover:scale-110 hover:-rotate-90 active:scale-90"
            >
              <X className="w-5 h-5 text-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Profilo</h1>
          </div>
        </header>

        {/* Profile Content */}
        <main className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center gap-4 mb-8">
            {/* Avatar with edit capability */}
            <div className="relative group">
              <button
                onClick={handleAvatarClick}
                disabled={isSaving}
                className="w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden transition-all duration-300 hover:scale-105 active:scale-95 ring-2 ring-transparent hover:ring-primary/50"
              >
                {profile?.avatar_url ? (
                  <img 
                    src={profile.avatar_url} 
                    alt="Avatar" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-muted-foreground" />
                )}
              </button>
              <div className="absolute bottom-0 right-0 p-1.5 bg-primary rounded-full text-primary-foreground shadow-lg transition-all duration-300 group-hover:scale-110">
                <Camera className="w-4 h-4" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Name with edit capability */}
            {isEditingName ? (
              <div className="flex flex-col items-center gap-3 w-full max-w-xs">
                <input
                  type="text"
                  value={editedUsername}
                  onChange={(e) => setEditedUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
                  placeholder="Username (obbligatorio per la Bacheca)"
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary"
                  maxLength={30}
                />
                <input
                  type="text"
                  value={editedFirstName}
                  onChange={(e) => setEditedFirstName(e.target.value)}
                  placeholder="Nome (opzionale)"
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="text"
                  value={editedLastName}
                  onChange={(e) => setEditedLastName(e.target.value)}
                  placeholder="Cognome (opzionale)"
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleSaveName}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  <span>Salva</span>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <button
                  onClick={handleEditName}
                  className="group flex items-center gap-2 transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  <h2 className="text-2xl font-bold text-foreground">{fullName}</h2>
                  <Pencil className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
                {profile?.username && (
                  <p className="text-sm text-primary font-medium">@{profile.username}</p>
                )}
                {!profile?.username && (
                  <button onClick={handleEditName} className="text-xs text-primary hover:underline">
                    Imposta username per la Bacheca
                  </button>
                )}
              </div>
            )}

            {/* Email */}
            <p className="text-sm text-muted-foreground">{email}</p>

          </div>

          {/* Menu Options */}
          <div className="space-y-3">
            {/* 1. Impostazioni */}
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-500/20 to-blue-600/10 hover:from-blue-500/30 hover:to-blue-600/20 border border-blue-500/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Settings className="w-5 h-5 text-blue-400" />
              <span className="font-medium bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Impostazioni</span>
              <ChevronRight className="w-5 h-5 text-blue-400/60 ml-auto" />
            </button>

            {/* 2. Notifiche Push */}
            {pushSupported && (
              <button
                onClick={toggleSubscription}
                disabled={pushLoading}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 ${
                  pushSubscribed
                    ? "bg-gradient-to-r from-green-500/20 to-emerald-600/10 border border-green-500/20"
                    : "bg-gradient-to-r from-orange-500/20 to-amber-600/10 border border-orange-500/20"
                }`}
              >
                {pushSubscribed ? (
                  <Bell className="w-5 h-5 text-green-400" />
                ) : (
                  <BellOff className="w-5 h-5 text-orange-400" />
                )}
                <div className="flex flex-col items-start flex-1">
                  <span className={`font-medium ${pushSubscribed ? "text-green-400" : "text-orange-400"}`}>
                    Notifiche Push
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {pushLoading ? "Caricamento..." : pushSubscribed ? "Attive — ricevi aggiornamenti su prodotti, news e messaggi" : "Disattivate — attiva per non perdere nessuna novità"}
                  </span>
                </div>
                {!pushLoading && (
                  <Badge variant={pushSubscribed ? "default" : "outline"} className={pushSubscribed ? "bg-green-500/80 text-white" : "border-orange-400/50 text-orange-400"}>
                    {pushSubscribed ? "ON" : "OFF"}
                  </Badge>
                )}
              </button>
            )}

            {/* 3. Contattaci */}
            <a
              href="mailto:assistenza.app26@gmail.com"
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <MessageCircle className="w-5 h-5 text-foreground" />
              <span className="text-foreground font-medium">Contattaci</span>
            </a>


            {/* 5. Stato Ordine */}
            <button
              onClick={() => setIsUserOrdersOpen(true)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <ShoppingBag className="w-5 h-5 text-foreground" />
              <span className="text-foreground font-medium">Stato Ordine</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </button>

            {/* 6. Metodi di Pagamento */}
            <button
              onClick={() => setIsPaymentMethodsOpen(true)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Wallet className="w-5 h-5 text-foreground" />
              <span className="text-foreground font-medium">Metodi di Pagamento</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </button>


            {/* 8. Modifica Password */}
            <button
              onClick={() => setIsChangePasswordOpen(true)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <KeyRound className="w-5 h-5 text-foreground" />
              <span className="text-foreground font-medium">Modifica Password</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </button>

            {/* 9. Esci */}
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                toast.success("Logout effettuato");
                navigate("/auth");
              }}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-destructive/10 hover:bg-destructive/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <LogOut className="w-5 h-5 text-destructive" />
              <span className="text-destructive font-medium">Esci</span>
            </button>

            {/* Admin Panel */}
            {isAdmin && (
              <>
                <button
                  onClick={() => setIsNotificationPanelOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Send className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Invia Notifiche Push</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsAdminOrdersOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <ShoppingBag className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Ordini Clienti</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsNewsManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Newspaper className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Gestione News</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsCategoryManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <FolderTree className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Gestione Categorie News</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsProductCategoryManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Package className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Gestione Categorie Prodotti</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsWallpaperCategoryManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <ImageIcon className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Gestione Categorie Sfondi</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsWallpaperDeleteManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-destructive/10 hover:bg-destructive/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Trash2 className="w-5 h-5 text-destructive" />
                  <span className="text-destructive font-medium">Elimina Sfondi</span>
                  <ChevronRight className="w-5 h-5 text-destructive/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsIPhoneImageManagerOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <SmartphoneIcon className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Immagini iPhone</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>

                <button
                  onClick={() => setIsAdminUsersOpen(true)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <UsersIcon className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Tabella Utenti</span>
                  <ChevronRight className="w-5 h-5 text-primary/60 ml-auto" />
                </button>
              </>
            )}
          </div>
        </main>

        {/* Bottom Navigation */}
        {userId && (
          <>
            <BottomNavigation onMenuClick={() => setIsMenuOpen(true)} activeTab="profile" />
            <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />
          </>
        )}

        {/* Settings Drawer */}
        <Drawer open={isSettingsOpen} onOpenChange={setIsSettingsOpen} shouldScaleBackground={false}>
          <DrawerContent className="h-full bg-background/[0.78] backdrop-blur-lg border-none">
            <DrawerHeader className="relative">
              <button 
                onClick={() => setIsSettingsOpen(false)}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
              >
                <ArrowLeft className="w-6 h-6 text-foreground" />
              </button>
              <DrawerTitle className="text-2xl font-bold text-center">Impostazioni</DrawerTitle>
            </DrawerHeader>
            <div className="flex flex-col gap-4 p-6">
              {/* Location Permission */}
              <button
                onClick={handleLocationPermission}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                <MapPin className="w-5 h-5 text-foreground" />
                <div className="flex flex-col items-start flex-1">
                  <span className="text-foreground font-medium">Posizione</span>
                  <span className="text-xs text-muted-foreground">
                    {locationPermission === 'granted' ? 'Attivata' : locationPermission === 'denied' ? 'Negata' : 'Non configurata'}
                  </span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>

              {/* Push Notifications */}
              {pushSupported && (
                <button
                  onClick={toggleSubscription}
                  disabled={pushLoading}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                >
                  {pushSubscribed ? (
                    <Bell className="w-5 h-5 text-primary" />
                  ) : (
                    <BellOff className="w-5 h-5 text-foreground" />
                  )}
                  <div className="flex flex-col items-start flex-1">
                    <span className="text-foreground font-medium">Notifiche Push</span>
                    <span className="text-xs text-muted-foreground">
                      {pushLoading ? 'Caricamento...' : pushSubscribed ? 'Attive' : 'Disattivate'}
                    </span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </button>
              )}

              {/* Clear Cache */}
              <button
                onClick={handleClearCache}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                <Trash2 className="w-5 h-5 text-foreground" />
                <span className="text-foreground font-medium">Svuota cache</span>
                <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
              </button>

              {/* Theme Selection */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground px-1">Tema App</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setTheme("classic")}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${
                      themePreference === "classic"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border/50 bg-muted/50 text-foreground hover:bg-muted"
                    }`}
                  >
                    <Sun className="w-5 h-5" />
                    <span className="text-xs font-medium">Classico</span>
                  </button>
                  <button
                    onClick={() => setTheme("dark")}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${
                      themePreference === "dark"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border/50 bg-muted/50 text-foreground hover:bg-muted"
                    }`}
                  >
                    <Moon className="w-5 h-5" />
                    <span className="text-xs font-medium">Dark Mode</span>
                  </button>
                  <button
                    onClick={() => setTheme("auto")}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${
                      themePreference === "auto"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border/50 bg-muted/50 text-foreground hover:bg-muted"
                    }`}
                  >
                    <Clock className="w-5 h-5" />
                    <span className="text-xs font-medium">Automatico</span>
                  </button>
                </div>
                {themePreference === "auto" && (
                  <p className="text-xs text-muted-foreground px-1">
                    Classico 06:00–18:00, Dark Mode 18:00–06:00
                  </p>
                )}
              </div>

              {/* Messaging Toggle */}
              <button
                onClick={async () => {
                  if (!userId || !profile) return;
                  const newVal = !profile.messaging_enabled;
                  const { error } = await supabase
                    .from("profiles")
                    .update({ messaging_enabled: newVal } as any)
                    .eq("user_id", userId);
                  if (!error) {
                    setProfile(prev => prev ? { ...prev, messaging_enabled: newVal } : null);
                    toast.success(newVal ? "Messaggi attivati" : "Messaggi disattivati");
                  }
                }}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                <MessageCircle className="w-5 h-5 text-foreground" />
                <div className="flex flex-col items-start flex-1">
                  <span className="text-foreground font-medium">Messaggi dal Profilo</span>
                  <span className="text-xs text-muted-foreground">
                    {profile?.messaging_enabled !== false ? "Gli altri utenti possono inviarti messaggi" : "Nessuno può inviarti messaggi dal profilo"}
                  </span>
                </div>
                <Badge variant={profile?.messaging_enabled !== false ? "default" : "outline"} className={profile?.messaging_enabled !== false ? "bg-primary/80 text-primary-foreground" : "border-muted-foreground/50 text-muted-foreground"}>
                  {profile?.messaging_enabled !== false ? "ON" : "OFF"}
                </Badge>
              </button>

            </div>
          </DrawerContent>
        </Drawer>


        {/* Saved Payment Methods Drawer */}
        <SavedPaymentMethods
          isOpen={isPaymentMethodsOpen}
          onClose={() => setIsPaymentMethodsOpen(false)}
        />

        {/* Change Password Drawer */}
        <Drawer open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen} shouldScaleBackground={false}>
          <DrawerContent className="h-full bg-background/[0.78] backdrop-blur-lg border-none">
            <DrawerHeader className="relative">
              <button 
                onClick={() => setIsChangePasswordOpen(false)}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
              >
                <ArrowLeft className="w-6 h-6 text-foreground" />
              </button>
              <DrawerTitle className="text-2xl font-bold text-center">Modifica Password</DrawerTitle>
            </DrawerHeader>
            <div className="flex flex-col gap-4 p-6 max-w-md mx-auto w-full">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Nuova Password</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Inserisci la nuova password"
                  className="bg-muted/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Conferma Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Conferma la nuova password"
                  className="bg-muted/50"
                />
              </div>
              <button
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className="w-full py-3 px-4 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isChangingPassword ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground" />
                ) : (
                  <>
                    <KeyRound className="w-5 h-5" />
                    Cambia Password
                  </>
                )}
              </button>
            </div>
          </DrawerContent>
        </Drawer>

        {/* Admin Notification Panel Drawer */}
        {isAdmin && (
          <Drawer open={isNotificationPanelOpen} onOpenChange={setIsNotificationPanelOpen} shouldScaleBackground={false}>
            <DrawerContent className="h-full bg-background/[0.78] backdrop-blur-lg border-none">
              <DrawerHeader className="relative">
                <button 
                  onClick={() => setIsNotificationPanelOpen(false)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
                >
                  <ArrowLeft className="w-6 h-6 text-foreground" />
                </button>
                <DrawerTitle className="text-2xl font-bold text-center">Invia Notifica Push</DrawerTitle>
              </DrawerHeader>
              <div className="flex flex-col gap-4 p-6 max-w-md mx-auto w-full">
                <div className="p-4 bg-primary/10 rounded-xl">
                  <p className="text-sm text-primary">
                    <Bell className="w-4 h-4 inline mr-2" />
                    Questa notifica verrà inviata a tutti gli utenti iscritti alle notifiche push.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Titolo</label>
                  <Input
                    type="text"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    placeholder="es. Nuovo Prodotto Disponibile!"
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Messaggio</label>
                  <Input
                    type="text"
                    value={notificationBody}
                    onChange={(e) => setNotificationBody(e.target.value)}
                    placeholder="es. iPhone 17 Pro ora disponibile con sconto!"
                    className="bg-muted/50"
                  />
                </div>
                <button
                  onClick={sendAdminPushNotification}
                  disabled={sendingNotification || !notificationTitle.trim() || !notificationBody.trim()}
                  className="w-full py-3 px-4 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingNotification ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Invia a Tutti gli Utenti
                    </>
                  )}
                </button>
              </div>
            </DrawerContent>
          </Drawer>
        )}

        {/* Category Manager Drawer */}
        <CategoryManager 
          isOpen={isCategoryManagerOpen} 
          onClose={() => setIsCategoryManagerOpen(false)} 
        />

        {/* Product Category Manager Drawer */}
        <ProductCategoryManager 
          isOpen={isProductCategoryManagerOpen} 
          onClose={() => setIsProductCategoryManagerOpen(false)} 
        />

        {/* User Orders Drawer */}
        <Drawer open={isUserOrdersOpen} onOpenChange={setIsUserOrdersOpen}>
          <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
            <DrawerHeader className="relative border-b border-border/30 pb-4">
              <button
                onClick={() => setIsUserOrdersOpen(false)}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/50 transition-all duration-300 hover:scale-110 active:scale-90"
              >
                <ArrowLeft className="w-5 h-5 text-foreground" />
              </button>
              <DrawerTitle className="text-xl font-bold text-center">Stato Ordine</DrawerTitle>
            </DrawerHeader>
            <UserOrders />
          </DrawerContent>
        </Drawer>

        {/* Admin Orders Drawer */}
        {isAdmin && (
          <Drawer open={isAdminOrdersOpen} onOpenChange={setIsAdminOrdersOpen}>
            <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
              <DrawerHeader className="relative border-b border-border/30 pb-4">
                <button
                  onClick={() => setIsAdminOrdersOpen(false)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/50 transition-all duration-300 hover:scale-110 active:scale-90"
                >
                  <ArrowLeft className="w-5 h-5 text-foreground" />
                </button>
                <DrawerTitle className="text-xl font-bold text-center">Ordini Clienti</DrawerTitle>
              </DrawerHeader>
              <AdminOrders />
            </DrawerContent>
          </Drawer>
        )}

        {/* News Manager Drawer */}
        {isAdmin && (
          <Drawer open={isNewsManagerOpen} onOpenChange={setIsNewsManagerOpen}>
            <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
              <DrawerHeader className="relative border-b border-border/30 pb-4">
                <button
                  onClick={() => setIsNewsManagerOpen(false)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/50 transition-all duration-300 hover:scale-110 active:scale-90"
                >
                  <ArrowLeft className="w-5 h-5 text-foreground" />
                </button>
                <DrawerTitle className="text-xl font-bold text-center">Gestione News</DrawerTitle>
              </DrawerHeader>
              <NewsManager />
            </DrawerContent>
          </Drawer>
        )}

        {/* Wallpaper Category Manager */}
        {isAdmin && (
          <WallpaperCategoryManager
            isOpen={isWallpaperCategoryManagerOpen}
            onClose={() => setIsWallpaperCategoryManagerOpen(false)}
          />
        )}

        {/* iPhone Image Manager */}
        {isAdmin && (
          <IPhoneImageManager
            isOpen={isIPhoneImageManagerOpen}
            onClose={() => setIsIPhoneImageManagerOpen(false)}
          />
        )}

        {/* Wallpaper Delete Manager */}
        {isAdmin && (
          <WallpaperDeleteManager
            isOpen={isWallpaperDeleteManagerOpen}
            onClose={() => setIsWallpaperDeleteManagerOpen(false)}
          />
        )}

        {/* Admin Users Table Drawer */}
        {isAdmin && (
          <Drawer open={isAdminUsersOpen} onOpenChange={setIsAdminUsersOpen}>
            <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
              <DrawerHeader className="border-b border-border/50">
                <DrawerTitle className="flex items-center gap-2">
                  <UsersIcon className="w-5 h-5 text-primary" />
                  Tabella Utenti
                </DrawerTitle>
              </DrawerHeader>
              <AdminUsersTable />
            </DrawerContent>
          </Drawer>
        )}

      </div>
    </PageTransition>
  );
};

export default Profile;
